import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { CartRequestEvent, getActiveCartRequests, getCartFromSections } from '@theme/caliart-cart-state';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  #requestObserver = null;
  #requestTimer;
  #confirmationTimer;
  #requestBusy = false;
  #requestSlow = false;
  #requestError = '';
  #expectedCart = null;
  #needsReconcile = false;
  #reconcileVersion = 0;
  #reconcileAbortController = null;
  #lockedCheckoutButtons = new Map();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartRequestEvent, this.#handleCartRequest);
    this.#requestObserver = new MutationObserver(() => this.#syncRequestView());
    this.#requestObserver.observe(this, { childList: true, subtree: true });
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
    if (getActiveCartRequests().length) this.#startRequestView();
    else if (this.#requestBusy) {
      // An instance can be detached while a request finishes; recover with a read, not another add.
      this.#requestError = '';
      this.#needsReconcile = true;
      this.#reconcileCart();
      this.#syncRequestView();
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncRequestView();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(CartRequestEvent, this.#handleCartRequest);
    this.#requestObserver?.disconnect();
    clearTimeout(this.#requestTimer);
    clearTimeout(this.#confirmationTimer);
    this.#confirmationTimer = undefined;
    this.#reconcileAbortController?.abort();
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    // This request already opened the drawer. Respect a subsequent close/Escape/back.
    if (this.hasAttribute('auto-open') && !event.detail?.data?.cartRequestId) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  #handleCartRequest = (event) => {
    const detail = event.detail;
    if (!detail?.token?.id) return;
    if (detail.phase === 'start') {
      this.#startRequestView();
      return;
    }
    if (detail.phase !== 'finish') return;
    if (detail.error) this.#requestError = detail.error;
    if (detail.cart) this.#expectedCart = detail.cart;
    if (getActiveCartRequests().length) return;
    if (this.#requestError || !this.#expectedCart) {
      this.#requestError ||= Theme.translations.cartRequestError;
      this.#syncRequestView();
    } else if (this.#needsReconcile) {
      this.#reconcileCart();
      this.#syncRequestView();
    } else {
      this.#syncRequestView();
    }
  };

  #startRequestView() {
    if (!this.#requestBusy || getActiveCartRequests().length === 1) {
      this.#requestError = '';
      this.#requestSlow = false;
      this.#expectedCart = null;
      this.#needsReconcile = false;
      clearTimeout(this.#requestTimer);
      clearTimeout(this.#confirmationTimer);
      this.#confirmationTimer = undefined;
      this.#requestTimer = setTimeout(() => {
        if (!this.#requestBusy || this.#requestError) return;
        this.#requestSlow = true;
        this.#syncRequestView();
      }, 8000);
    }
    this.#requestBusy = true;
    this.#reconcileVersion++;
    this.#reconcileAbortController?.abort();
    if (getActiveCartRequests().length > 1) this.#needsReconcile = true;
    this.#syncRequestView();
    if (this.hasAttribute('auto-open') && !this.refs.dialog.open) this.showDialog({ immediate: true });
  }

  /** Compare the displayed cart, including pricing, before restoring checkout. */
  #cartSignature(cart) {
    if (!Array.isArray(cart?.items) || !Number.isSafeInteger(cart.item_count)) return null;
    return JSON.stringify({
      count: cart.item_count, total: cart.total_price, discount: cart.total_discount, currency: cart.currency,
      lines: cart.items.map(item => [item.key, item.variant_id, item.quantity, item.final_line_price,
        item.original_line_price, item.total_discount]).sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
      discounts: (cart.cart_level_discount_applications ?? []).map(discount =>
        [discount.title, discount.total_allocated_amount]).sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
    });
  }

  #displayedCartMatches() {
    const snapshot = this.querySelector('cart-items-component script[data-caliart-cart-state]');
    if (!snapshot?.textContent || !this.#expectedCart) return false;
    try {
      const actual = this.#cartSignature(JSON.parse(snapshot.textContent));
      return actual !== null && actual === this.#cartSignature(this.#expectedCart);
    } catch {
      return false;
    }
  }

  #syncRequestView() {
    const active = getActiveCartRequests().length > 0;
    if (active || this.#requestError) {
      clearTimeout(this.#confirmationTimer);
      this.#confirmationTimer = undefined;
    }
    if (this.#requestBusy && !active && !this.#requestError && !this.#needsReconcile && this.#displayedCartMatches()) {
      this.#requestBusy = false;
      this.#requestSlow = false;
      clearTimeout(this.#requestTimer);
      clearTimeout(this.#confirmationTimer);
      this.#confirmationTimer = undefined;
    } else if (this.#requestBusy && !active && !this.#requestError && this.#confirmationTimer === undefined) {
      // A confirmed POST must not leave the shopper stuck if its display refresh fails.
      this.#confirmationTimer = setTimeout(() => {
        this.#confirmationTimer = undefined;
        if (!this.#requestBusy || getActiveCartRequests().length) return;
        this.#reconcileVersion++;
        this.#reconcileAbortController?.abort();
        this.#requestError = Theme.translations.cartRequestError;
        this.#syncRequestView();
      }, 4000);
    }
    const panel = this.querySelector('[data-cart-request-panel]');
    const items = this.querySelector('cart-items-component');
    if (!panel || !items) return;
    const restoreFocus = !this.#requestBusy && this.refs.dialog.open &&
      !this.refs.dialog.classList.contains('dialog-closing') && panel.contains(document.activeElement);
    panel.hidden = !this.#requestBusy;
    this.refs.dialog.toggleAttribute('data-cart-request-pending', this.#requestBusy);
    items.toggleAttribute('inert', this.#requestBusy);
    if (this.#requestBusy) items.setAttribute('aria-hidden', 'true');
    else items.removeAttribute('aria-hidden');
    const message = panel.querySelector('[data-cart-request-message]');
    const recovery = panel.querySelector('[data-cart-request-recovery]');
    if (recovery) recovery.hidden = !this.#requestError;
    panel.toggleAttribute('data-cart-request-error', !!this.#requestError);
    const text = this.#requestBusy
      ? this.#requestError || (this.#requestSlow ? Theme.translations.cartRequestSlow : Theme.translations.addingToCart)
      : Theme.translations.addingToCart;
    if (message && message.textContent !== text) message.textContent = text;
    if (this.#requestBusy) {
      this.refs.dialog.setAttribute('aria-labelledby', 'cart-drawer-request-heading');
      for (const button of items.querySelectorAll('[name="checkout"]')) {
        if (!(button instanceof HTMLButtonElement)) continue;
        if (!this.#lockedCheckoutButtons.has(button)) this.#lockedCheckoutButtons.set(button, button.disabled);
        button.disabled = true;
      }
    } else {
      this.refs.dialog.setAttribute('aria-labelledby', this.refs.dialog.classList.contains('cart-drawer--empty')
        ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
      for (const [button, disabled] of this.#lockedCheckoutButtons) if (button.isConnected) button.disabled = disabled;
      this.#lockedCheckoutButtons.clear();
      if (restoreFocus) items.querySelector('[ref="closeButton"]')?.focus({ preventScroll: true });
    }
  }

  /** Concurrent requests can arrive out of order: reconcile once, never repeat the POST. */
  async #reconcileCart() {
    const version = ++this.#reconcileVersion;
    this.#reconcileAbortController = new AbortController();
    try {
      const url = new URL(Theme.routes.cart_url, location.origin);
      url.searchParams.set('sections', 'caliart-cart-drawer');
      const response = await fetch(url, { cache: 'no-store', signal: this.#reconcileAbortController.signal });
      if (!response.ok) throw new Error('Unable to refresh cart.');
      const sections = await response.json();
      const cart = getCartFromSections(sections);
      if (!cart) throw new Error('Missing cart snapshot.');
      if (version !== this.#reconcileVersion || this.isConnected === false || getActiveCartRequests().length) return;
      this.#expectedCart = cart;
      this.#needsReconcile = false;
      document.dispatchEvent(new CartUpdateEvent(cart, 'caliart-cart-request', {
        source: 'caliart-cart-request', sections, itemCount: cart.item_count, cartRequestId: 'reconcile',
      }));
      this.#syncRequestView();
    } catch (error) {
      if (version !== this.#reconcileVersion || this.isConnected === false) return;
      this.#requestError = Theme.translations.cartRequestError;
      this.#syncRequestView();
    }
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // The compact footer is a separate flex row; expanding options never changes its position.
    if (dialog.hasAttribute('data-checkout-fixed')) {
      dialog.setAttribute('cart-summary-sticky', 'true');
      return;
    }

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
